import ShopImages from '@activities/shops/ShopDetailBottomSheet/ShopImages';
import AuthGuard from '@components/AuthGuard';
import BottomSheetHeader from '@components/BottomSheetHeader';
import Column from '@components/Column';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import useCopyShopAddress from '@hooks/shop/useCopyShopAddress';
import useShop from '@hooks/shop/useShop';
import useAppCustom from '@hooks/useAppCustom';
import Copy from '@icons/Copy';
import FavoriteFill from '@icons/favorite/FavoriteFill';
import PhoneFill from '@icons/phone/PhoneFill';
import { BottomSheet } from '@stackflow/plugin-basic-ui';
import { ActivityComponentType } from '@stackflow/react';
import { useTranslation } from 'react-i18next';

import useToggleMyShop from './hooks/useToggleMyShop';
import OrderButton from './OrderButton';
import styles from './styles.module.scss';

interface Params {
    shopId: string;
}

const ShopDetailBottomSheet:ActivityComponentType<Params> = ({ params }: { params: Params }) => {
    const { shopId } = params;
    const { t } = useTranslation('shopDetail');
    const shop = useShop(shopId);
    const { toggleMyShop } = useToggleMyShop(shop);
    const copyAddress = useCopyShopAddress(shop?.address);
    const { useAddress, usePhoneNumber, useHashtag } = useAppCustom().shop;
    return (
        <BottomSheet>
            <Container>
                <BottomSheetHeader>
                    {t('header')}
                </BottomSheetHeader>
                <Column style={{ flex: 1, overflow: 'hidden' }}>
                    <ShopImages shop={shop} />
                    <Contents>
                        <Column className={styles.contents}
                            fullWidth
                            spacing={16}>
                            <Row justify='space-between'
                                fullWidth>
                                <Text size='3xl' className={styles.title}
                                    fontWeight='bold'>
                                    {shop?.name}
                                </Text>
                                <AuthGuard className={styles.favorite}
                                    flows={[
                                        { activityName: 'ShopListActivity', activityParams: { type: 'LIST' } },
                                        { activityName: 'ShopDetailBottomSheet', activityParams: { shopId } },
                                    ]}>
                                    <FavoriteFill size={28}
                                        onClick={toggleMyShop}
                                        color={shop?.isMyShop ? colors.red : colors.gray4} />
                                </AuthGuard>
                            </Row>
                            {useAddress && (
                                <Text size='lg'>
                                    {shop?.address}
                                </Text>
                            )}
                            <Row spacing={14}>
                                {useAddress && (
                                    <Row spacing={2} onClick={copyAddress}>
                                        <Copy color={colors.gray7}/>
                                        <Text color={colors.gray7}>{t('copyAddress')}</Text>
                                    </Row>
                                )}
                                {usePhoneNumber && (
                                    <a href={`tel:${shop?.phoneNumber}`}>
                                        <Row>
                                            <PhoneFill color={colors.gray7} size={16}/>
                                            <Text color={colors.gray7}>{t('callInquiry')}</Text>
                                        </Row>
                                    </a>
                                )}
                            </Row>
                            {useHashtag && (
                                <HashTags>
                                    {shop?.hashtag.map((tag, i) => (
                                        <Text key={`${tag} ${i}`} color={colors.gray6}>
                                            #{tag}
                                        </Text>
                                    ))}
                                </HashTags>
                            )}
                            <div className={styles.divider}/>
                            {(shop?.phoneNumber && usePhoneNumber) && (
                                <Row spacing={8}>
                                    <Text color={colors.primary} fontWeight='bold'>{t('shopPhone')}</Text>
                                    <Text >{shop?.phoneNumber}</Text>
                                </Row>
                            )}
                            {shop?.openhours && (
                                <Row spacing={8} align='flex-start'>
                                    <Text color={colors.primary} fontWeight='bold'>{t('openHours')}</Text>
                                    <Text className={styles.openHours}>
                                        {shop?.openhours}
                                    </Text>
                                </Row>
                            )}
                        </Column>
                        <OrderButton shopId={shopId}
                            shopStatus={shop?.shopStatus || 'PENDING'}
                            status={shop?.orderStatus || 'CLOSED'} />
                    </Contents>
                </Column>
            </Container>
        </BottomSheet>
    );
};

const Container = styled.div`
    max-height: 100vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`;

const Contents = styled.div`
    flex: 1;
    overflow: hidden;
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const HashTags = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    width: 100%;
`;

export default ShopDetailBottomSheet;
